import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { Permission, Resource } from '@pepperhq/auth-client';
import { LoyaltyProviderEnum } from 'components/loyalty/enums';

import logger from 'lib/logger';
import { Option } from 'lib/types';
import { ApplicationState } from 'store/store';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { arrayOrNull, objectOrNull, parseIntOrNull, parseIntOrUndefined, stringOrNull } from 'lib/helpers';
import { ILocationScopedSettingsComponentProps } from './LocationScopedSettings';
import { getPublicAndPrivateSettings } from 'store/settings/settingsActions';
import { DEFAULT_MAX_USER_METRICS } from 'components/metrics/model/loyalty-tier';
import { CRMSettingsCard } from './MarketingSettings/CRMSettingsCard';
import { ReferralsSettingsCard } from './MarketingSettings/ReferralsSettingsCard';
import { LoyaltyCardInWalletSettings } from './MarketingSettings/LoyaltyCardInWalletSettings';
import { CouponProvider, LoyaltySettingsCard } from './MarketingSettings/LoyaltySettingsCard';
import { NotificationsSettingsCard } from './MarketingSettings/NotificationsSettingsCard';
import { BookingsSettingsCard } from './MarketingSettings/BookingsSettingsCard';

export const MarketingSettings: React.FC<ILocationScopedSettingsComponentProps> = ({
    settings,
    onSettingsSaved,
    locationId,
    globalSettings
}) => {
    const { tenant, claims } = useSelector((state: ApplicationState) => state.auth);
    const isSquarePos = React.useMemo(() => settings?.posProvider === 'SQUARE', [settings]);
    const dispatch = useDispatch();

    const internalSettingsVisible = React.useMemo(
        () => claims.hasPermission(Resource.InternalSetting, Permission.read),
        [claims]
    );
    const internalSettingsEditable = React.useMemo(
        () => claims.hasPermission(Resource.InternalSetting, Permission.write),
        [claims]
    );

    const globalLoyaltyProvider = React.useMemo(() => {
        if (locationId === undefined) {
            return settings?.loyaltyProvider;
        }
        return globalSettings?.loyaltyProvider;
    }, [locationId, globalSettings, settings]);

    const globalGiftCardProvider = React.useMemo(() => {
        if (locationId === undefined) {
            return settings?.giftCardPaymentProvider;
        }
        return globalSettings?.giftCardPaymentProvider;
    }, [locationId, globalSettings, settings]);

    const globalCouponProvider = React.useMemo(() => {
        if (locationId === undefined) {
            return settings?.couponProvider;
        }
        return globalSettings?.couponProvider;
    }, [locationId, globalSettings, settings]);

    const handleSubmitNotifications = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};

                // Common Settings
                if (locationId === undefined) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        apns: {
                            teamId: values.appleTeamId,
                            keyId: values.appleKeyId,
                            keyAsBase64: values.appleKeyFile
                        },
                        app: {
                            bundleIdentifier: values.appleBundleIdentifier
                        },
                        actionNotifications: {
                            ORDER_CREATED: { email: !!values.orderCreated },
                            ORDER_USER_BILLED: { email: !!values.orderUserBilled },
                            ORDER_COMPLETED: { email: !!values.orderCompleted },
                            USER_ACTIVATED: { email: !!values.userActivated }
                        }
                    };
                }
                if (Object.keys(settingsToUpdate).length === 0) {
                    // No need to update nothing
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);

                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [onSettingsSaved, locationId]
    );

    const handleSubmitLoyalty = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};

                // Common Settings
                if (locationId === undefined) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        loyaltyEnabled: !!values.loyaltyEnabled,
                        loyaltyProvider: values.loyaltyProvider,
                        giftCardPaymentEnabled: values.giftCardEnabled,
                        giftCardPaymentProvider: values.giftCardProvider,
                        giftCardPayments: {
                            tippingIncluded: values.giftCardTippingIncluded
                        },
                        physicalStampsEnabled: values.soloStampsEnabled,
                        snowShoe: {
                            key: values.snowshoeKey
                        },
                        loyalty: {
                            showBalanceAward: values.loyaltyShowBalanceAward,
                            displayExternalMemberNumber: values.loyaltyDisplayExternalMemberNumber,
                            recalculateServiceChargeOnDiscount:
                                values.loyaltyRecalculateServiceChargeOnDiscount,
                            useInternalAutoAwardApplication: values.loyaltyUseInternalAutoAwardApplication,
                            multipleAwardRedemptionsEnabled: !!values.loyaltyMultipleAwardRedemptionsEnabled
                        },
                        userDeletion: {
                            ...values?.userDeletion,
                            deleteLoyaltyAccountOnUserDeletion:
                                values.userDeletion.deleteLoyaltyAccountOnUserDeletion
                        },
                        app: {
                            awardsScreenBarcodePosition: values.awardsScreenBarcodePosition
                        },
                        ...(internalSettingsEditable && {
                            premiumLoyalty: {
                                maximumNumberOfUserMetrics:
                                    parseIntOrUndefined(values.maximumNumberOfUserMetrics) ??
                                    DEFAULT_MAX_USER_METRICS
                            }
                        })
                    };
                    if (internalSettingsEditable) {
                        settingsToUpdate.premiumLoyaltyEnabled = values.premiumLoyaltyEnabled;
                        settingsToUpdate.scheduledNotificationsEnabled = values.scheduledNotificationsEnabled;
                        settingsToUpdate.triggeredNotificationsEnabled = values.triggeredNotificationsEnabled;
                    }
                }
                // We pass couponProvider in both Global & Location settings
                settingsToUpdate = {
                    ...settingsToUpdate,
                    couponProvider: values.couponProvider
                };

                switch (values.couponProvider) {
                    case CouponProvider.EAGLE_EYE: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                eagleEye: {
                                    isProduction: values.eagleEyeIsProduction,
                                    clientId: values.eagleEyeClientId,
                                    secret: values.eagleEyeSecret
                                }
                            };
                        }
                        break;
                    }
                    case CouponProvider.THREES_POS: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                threesPos: {
                                    host: values.threesPosHost,
                                    licenceKey: values.threesPosLicenceKey,
                                    providerId: parseIntOrNull(values.threesPosProviderId)
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                threesPos: {
                                    webOrderTypeMapping: {
                                        PREORDER: parseIntOrNull(values.threesPosPreorderMapping),
                                        ORDER_TO_TABLE: parseIntOrNull(values.threesPosOrderToTableMapping),
                                        PAY_AT_TABLE: parseIntOrNull(values.threesPosPayAtTableMapping),
                                        DINE_IN: parseIntOrNull(values.threesPosDineInMapping),
                                        DELIVERY: parseIntOrNull(values.threesPosDeliveryMapping)
                                    },
                                    locationId: parseIntOrNull(values.threesPosLocationId),
                                    providerId: parseIntOrNull(values.threesPosProviderId)
                                }
                            };
                        }
                        break;
                    }
                }

                switch (values.loyaltyProvider) {
                    case LoyaltyProviderEnum.POWERCARD: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                powercardLoyalty: {
                                    isProduction: values.powercardIsProduction,
                                    apiToken: values.powercardApiToken,
                                    m_id: values.powercardMerchantId
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                powercardLoyalty: {
                                    l_id: values.powercardLocationId
                                }
                            };
                        }
                        break;
                    }
                    case LoyaltyProviderEnum.ATREEMO: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                atreemo: {
                                    host: values.atreemoHost,
                                    username: values.atreemoUsername,
                                    password: values.atreemoPassword,
                                    brandId: parseIntOrNull(values.atreemoBrandId),
                                    loyaltyBalanceVoucherCategoryId: values.atreemoVoucherCategoryId,
                                    loyaltyBalanceRedemptionValue: values.atreemoVoucherRedemptionValue,
                                    useCyclePointsBalance: values.atreemoUseCycleBalance
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                atreemo: {
                                    locationId: values.atreemoLocationId
                                }
                            };
                        }
                        break;
                    }
                    case LoyaltyProviderEnum.PAYTRONIX: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                paytronix: {
                                    host: values.paytronixHost,
                                    username: values.paytronixUsername,
                                    password: values.paytronixPassword,
                                    merchantId: parseIntOrNull(values.paytronixMerchantId),
                                    cardTemplateCode: parseIntOrNull(values.paytronixCardTemplateCode),
                                    storeCode: values.paytronixStoreCode
                                }
                            };
                        }
                        break;
                    }
                }
                switch (values.giftCardProvider) {
                    case 'POWERCARD':
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                powercardGift: {
                                    isProduction: values.powercardGiftCardIsProduction,
                                    apiToken: values.powercardGiftCardApiToken,
                                    m_id: values.powercardGiftCardMerchantId,
                                    gift_pid: values.powercardGiftCardGiftPID
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                powercardGift: {
                                    l_id: values.powercardGiftCardLocationId
                                }
                            };
                        }
                        break;
                    case 'TOGGLE': {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                toggle: {
                                    username: values.toggleGiftCardUsername,
                                    password: values.toggleGiftCardPassword
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                toggle: {
                                    eposReference: values.toggleGiftCardEposReference
                                }
                            };
                        }
                        break;
                    }
                    case 'REVEL': {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                revel: {
                                    userId: values.revelGiftCardUserId
                                }
                            };
                        }
                        break;
                    }
                    case 'EAGLE_EYE': {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                eagleEye: {
                                    ...settingsToUpdate?.eagleEye,
                                    fullRedemptionOnlyCampaignIds:
                                        values.eagleEyeGiftCardFullRedemptionOnlyCampaignIds || []
                                }
                            };
                        }
                        break;
                    }
                }
                if (Object.keys(settingsToUpdate).length === 0) {
                    // No need to update nothing
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);
                if (!locationId && internalSettingsVisible) {
                    getPublicAndPrivateSettings(claims)(dispatch);
                }

                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [claims, dispatch, locationId, onSettingsSaved, internalSettingsEditable, internalSettingsVisible]
    );

    const handleSubmitReferrals = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};

                // Common Settings
                if (locationId === undefined) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        referralEnabled: values.referralEnabled,
                        referral: {
                            stampsPerReferer: parseIntOrUndefined(values.stampsPerReferer),
                            stampsPerReferee: parseIntOrUndefined(values.stampsPerReferee),
                            messageTemplate: values.messageTemplate
                        }
                    };
                }
                if (Object.keys(settingsToUpdate).length === 0) {
                    // No need to update nothing
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);

                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [onSettingsSaved, locationId]
    );

    const handleSubmitLoyaltyWallets = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};
                // Common Settings
                if (locationId === undefined) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        appleWalletEnabled: values.appleWalletEnabled,
                        appleWallet: {
                            passTypeIdentifier: stringOrNull(values.appleWalletPassTypeIdentifier),
                            description: stringOrNull(values.appleWalletDescription),
                            logoText: stringOrNull(values.appleWalletLogoText),
                            logoImage: stringOrNull(values.appleWalletLogo),
                            backgroundColor: stringOrNull(values.appleWalletBackgroundColor),
                            foregroundColor: stringOrNull(values.appleWalletForegroundColor),
                            labelColor: stringOrNull(values.appleWalletForegroundColor),
                            p12BundleAsBase64: stringOrNull(values.appleWalletP12BundleAsBase64)
                        },
                        googleWalletEnabled: values.googleWalletEnabled,
                        googleWallet: {
                            issuerId: stringOrNull(values.googleWalletIssuerId),
                            classId: stringOrNull(values.googleWalletClassId),
                            clientEmail: stringOrNull(values.googleWalletClientEmail)
                        },
                        wallet: objectOrNull({
                            column1: stringOrNull(values.walletColumn1),
                            column2: stringOrNull(values.walletColumn2),
                            detail: arrayOrNull(values.walletDetail.map((option: Option) => option.value))
                        })
                    };
                }
                if (Object.keys(settingsToUpdate).length === 0) {
                    // No need to update nothing
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);

                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [onSettingsSaved, locationId]
    );

    const handleSubmitCRM = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};

                // Common Settings
                if (locationId === undefined) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        revinate: {
                            isEnabled: values.revinateIsEnabled,
                            token: values.revinateToken,
                            pointsPerkId: values.revinatePointsPerkId
                        }
                    };
                }
                if (Object.keys(settingsToUpdate).length === 0) {
                    // No need to update nothing
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);

                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [onSettingsSaved, locationId]
    );

    const handleSubmitBookings = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                // Both Global & Location
                const settingsToUpdate: IPublicAndPrivateSettings = {
                    guestline: {
                        interfaceId: parseIntOrNull(values.interfaceId),
                        operatorCode: values.operatorCode,
                        password: values.password,
                        // Location Only
                        ...(locationId !== undefined && {
                            siteId: values.guestlineSiteId
                        }),
                        // Global Only
                        ...(locationId === undefined && {
                            bookingsPollingEnabled: values.bookingsPollingEnabled
                        })
                    }
                };

                if (Object.keys(settingsToUpdate).length === 0) {
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);

                await onSettingsSaved();
                return true;
            } catch (error) {
                logger.error(`Error Updating Settings: ${JSON.stringify(error)}`);
                return false;
            }
        },
        [onSettingsSaved, locationId]
    );

    return (
        <Box minWidth="500px">
            {/* Cards for each settings block */}
            {locationId === undefined && (
                <NotificationsSettingsCard settings={settings} onSubmit={handleSubmitNotifications} />
            )}
            {(locationId === undefined ||
                globalLoyaltyProvider === LoyaltyProviderEnum.POWERCARD ||
                globalGiftCardProvider === 'POWERCARD' ||
                globalLoyaltyProvider === LoyaltyProviderEnum.ATREEMO ||
                globalGiftCardProvider === 'TOGGLE' ||
                globalGiftCardProvider === 'REVEL' ||
                globalCouponProvider === CouponProvider.THREES_POS) && (
                <LoyaltySettingsCard
                    locationId={locationId}
                    settings={settings}
                    tenantId={tenant?.tenantId}
                    isSquarePos={isSquarePos}
                    internalSettingsEditable={internalSettingsEditable}
                    internalSettingsVisible={internalSettingsVisible}
                    globalLoyaltyProvider={globalLoyaltyProvider}
                    globalGiftCardProvider={globalGiftCardProvider}
                    onSubmit={handleSubmitLoyalty}
                />
            )}

            {locationId === undefined && (
                <LoyaltyCardInWalletSettings
                    settings={settings}
                    tenantId={tenant?.tenantId}
                    onSubmit={handleSubmitLoyaltyWallets}
                />
            )}

            {locationId === undefined && (
                <ReferralsSettingsCard
                    settings={settings}
                    onSubmit={handleSubmitReferrals}
                    tenantId={tenant?.tenantId}
                />
            )}
            {locationId === undefined && <CRMSettingsCard settings={settings} onSubmit={handleSubmitCRM} />}
            <BookingsSettingsCard
                settings={settings}
                onSubmit={handleSubmitBookings}
                locationId={locationId}
            />
        </Box>
    );
};
